/**
 * `useResource` — the one async-state machine every screen uses.
 * OWNER: web-frontend-engineer.
 *
 * It encodes MOTION_SPEC §3.3 / §6.3's three skeleton timings so no screen has
 * to remember them:
 *   1. below `motion.threshold.silent` (120 ms) show NOTHING — a skeleton that
 *      flashes for 80 ms is worse than no skeleton;
 *   2. once shown it stays at least `motion.threshold.skeletonMin` (200 ms);
 *   3. past `motion.threshold.slow` (5 s) a typeset line says so. Still no
 *      spinner, still no percentage — there is no spinner in this product.
 *
 * The thresholds are read from the GENERATED token module, not typed here
 * (BUILD_PIPELINE gate G1: no bare ms in web source).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { threshold } from '@pt/tokens';
import { ApiRejection, ApiUnreachable } from '@pt/api-client';

const SILENT = threshold.silent;
const SKELETON_MIN = threshold.skeletonMin;
const SLOW = threshold.slow;

export type ResourcePhase = 'idle' | 'quiet' | 'skeleton' | 'ready' | 'error' | 'offline';

export interface ResourceState<T> {
  readonly phase: ResourcePhase;
  readonly data: T | null;
  /** A server refusal — human copy renders, machine code is logged, never shown. */
  readonly rejection: ApiRejection | null;
  /** True past `motion.threshold.slow`. Drives a typeset line, never a spinner. */
  readonly slow: boolean;
  readonly reload: () => void;
  /** Replace the held value in place — used after a mutation that returns fresh data. */
  readonly set: (value: T) => void;
}

export function useResource<T>(load: () => Promise<T>, deps: readonly unknown[]): ResourceState<T> {
  const [phase, setPhase] = useState<ResourcePhase>('idle');
  const [data, setData] = useState<T | null>(null);
  const [rejection, setRejection] = useState<ApiRejection | null>(null);
  const [slow, setSlow] = useState(false);
  const [nonce, setNonce] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let live = true;
    const shownAt = { at: 0 };
    setSlow(false);
    setPhase((current) => (current === 'ready' ? 'quiet' : 'idle'));

    const silentTimer = window.setTimeout(() => {
      if (!live) return;
      shownAt.at = Date.now();
      setPhase((current) => (current === 'ready' ? current : 'skeleton'));
    }, SILENT);

    const slowTimer = window.setTimeout(() => {
      if (live) setSlow(true);
    }, SLOW);

    const settle = (apply: () => void) => {
      const held = shownAt.at === 0 ? 0 : Math.max(0, SKELETON_MIN - (Date.now() - shownAt.at));
      window.setTimeout(() => {
        if (live) apply();
      }, held);
    };

    loadRef
      .current()
      .then((value) => {
        settle(() => {
          setData(value);
          setRejection(null);
          setPhase('ready');
        });
      })
      .catch((error: unknown) => {
        settle(() => {
          if (error instanceof ApiUnreachable) {
            setPhase('offline');
          } else if (error instanceof ApiRejection) {
            setRejection(error);
            setPhase('error');
          } else {
            setRejection(null);
            setPhase('error');
          }
        });
      })
      .finally(() => {
        window.clearTimeout(silentTimer);
        window.clearTimeout(slowTimer);
      });

    return () => {
      live = false;
      window.clearTimeout(silentTimer);
      window.clearTimeout(slowTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const set = useCallback((value: T) => {
    setData(value);
    setPhase('ready');
  }, []);

  return { phase, data, rejection, slow, reload, set };
}

/** A write in flight. `ST-CTL-BUSY` — in place, same size, cannot double-submit. */
export interface Submission {
  readonly busy: boolean;
  readonly rejection: ApiRejection | null;
  readonly offline: boolean;
  readonly run: <T>(work: () => Promise<T>) => Promise<T | null>;
  readonly clear: () => void;
}

export function useSubmission(): Submission {
  const [busy, setBusy] = useState(false);
  const [rejection, setRejection] = useState<ApiRejection | null>(null);
  const [offline, setOffline] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(async <T,>(work: () => Promise<T>): Promise<T | null> => {
    if (inFlight.current) return null;
    inFlight.current = true;
    setBusy(true);
    setRejection(null);
    setOffline(false);
    try {
      return await work();
    } catch (error) {
      if (error instanceof ApiUnreachable) setOffline(true);
      else if (error instanceof ApiRejection) setRejection(error);
      else setOffline(true);
      return null;
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }, []);

  const clear = useCallback(() => {
    setRejection(null);
    setOffline(false);
  }, []);

  return { busy, rejection, offline, run, clear };
}
